import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { ApiError } from './lib/api';
import { reloadOnceForNewBuild } from './lib/staleChunk';
import './styles/index.css';
import './styles/theme-overrides.css';

// A deploy invalidates the previous build's chunk URLs; reload once instead
// of failing the import in tabs that were open before the deploy.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForNewBuild()) {
    event.preventDefault();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.status === 401 || error.code === 'SESSION_EXPIRED')) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>
);

// Clear the old app-shell service worker so deploys cannot serve stale frontend bundles.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
      .catch((error) => {
        console.log('SW cleanup failed:', error);
      });
  });
}
